<template>
	<div class="module reservation-window-card display-flex flex-direction-column">
		<div
			:class="[
				'display-flex justify-content-space-between px-150 pt-150 pb-50',
				isComingSoon ? 'bg-color-bg-secondary' : 'bg-color-greige'
			]"
		>
			<div class="pb-25">
				<h5 class="heading-5">{{ window.name }}</h5>
				<span v-if="showOpenDateRange" class="body-md">{{ window.openDateRange }}</span>
			</div>
			<StatusTag :text="windowStatusLabel" :background="windowStatusClass" />
		</div>
		<div class="p-150 display-flex flex-direction-column justify-content-space-between gap-150">
			<div class="display-flex flex-direction-column gap-150">
				<ProgressMeter
					v-if="showRemainingCapacity"
					class="window-progress-meter"
					height="8"
					:progress-percent="capacityFilled"
					:is-rounded="false"
					bar-class="bg-color-gold-light"
				>
					<template #left-side>
						<div>
							<span class="show-for-sr">Remaining Capacity</span>
						</div>
					</template>
					<template #right-side>
						<div>
							<span>{{ remainingCapacityDisplayText }}</span>
						</div>
					</template>
				</ProgressMeter>
				<div v-for="(info, i) in windowInfo" :key="i" class="body-md">
					<div class="font-weight-bold">{{ info.key }}</div>
					<div>{{ info.value }}</div>
				</div>
				<div v-if="isComingSoon" class="body-md">Details coming soon</div>
			</div>
			<div>
				<BaseButton
					v-if="showCtaButton"
					:classes="`button width-100 ${ctaButtonClasses}`"
					:disabled="isDisabled"
					:mixpanel-custom-properties="mixpanelCustomProperties"
					data-test="window-reservation-card-cta"
					@click="handleCtaClick()"
					>{{ ctaButtonText }}</BaseButton
				>
				<div v-else class="bg-color-greige p-100 width-100 text-align-center">
					This window is not currently accepting{{ '\xa0' }}reservations
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { date, percentage } from '@filters/shared-filters';
import { KeyValue, Link, TagBackground } from 'types/layout';
import { useRoute, useRouter } from 'vue-router';
import big from 'big.js';
import { computed } from 'vue';
import { getInvestFlowMixpanelCustomProperties } from '@utils/invest';
import { InvestmentTriggerQuery } from 'types/checkout';
import { mapInvestmentTrigger } from '@utils/checkout';
import { MixpanelContext } from 'types/analytics';
import ProgressMeter from '@components/layout/progress-meter.vue';
import { ReservationWindow } from 'types/reit-element';
import StatusTag from '@components/tags/status-tag.vue';
import { useInvestmentTriggerStore } from '@stores/investment-trigger';
import { useInvestStore } from '@stores/invest';

interface Props {
	window: ReservationWindow;
	createReservationLink: Link;
	isNextWindow?: boolean;
	isComingSoon?: boolean;
}

const investStore = useInvestStore();
const investmentTriggerStore = useInvestmentTriggerStore();

const props = withDefaults(defineProps<Props>(), { isNextWindow: false, isComingSoon: false });

const offeringId = computed((): string => investStore.investMetaData?.offering?.id ?? '');

const router = useRouter();

const route = useRoute();

const mixpanelCustomProperties = computed((): MixpanelContext => {
	return {
		...getInvestFlowMixpanelCustomProperties('window-reservation', offeringId.value)
	};
});

const showOpenDateRange = computed((): boolean => {
	return !!props.window.openDateRange;
});

const windowStatusLabel = computed((): string => {
	return props.window.status.label;
});

const windowStatusClass = computed((): TagBackground => {
	switch (props.window.status.name) {
		case 'AVAILABLE':
			return 'green';
		case 'FULL':
		case 'CLOSED':
		default:
			return 'default';
	}
});

const showRemainingCapacity = computed((): boolean => {
	return !!props.window.remainingCapacityPercentage;
});

const capacityFilled = computed((): string => {
	const remainingCapacity = big(props.window.remainingCapacityPercentage ?? '0').times(100);

	return big('100').sub(remainingCapacity).toString();
});

const remainingCapacityDisplayText = computed((): string => {
	const remainingCapacity = props.window.remainingCapacityPercentage ?? '0';

	return `${big(remainingCapacity).gt('0') ? '~' : ''}${percentage(remainingCapacity, true, 0)} remaining`;
});

const windowInfo = computed((): Array<KeyValue> => {
	const infoSections: Array<KeyValue> = [];

	if (props.window.projectedCapacity) {
		infoSections.push({ key: 'Projected amount', value: props.window.projectedCapacity });
	}
	infoSections.push({ key: 'Estimated transaction date', value: date(props.window.estimatedTransactionDate) });

	return infoSections;
});

const showCtaButton = computed((): boolean => {
	return props.window.status.name !== 'CLOSED';
});

const canCreateReservation = computed((): boolean => {
	return props.window.directInvestmentEligibility === 'RESERVATION_ELIGIBLE';
});

const isDisabled = computed((): boolean => {
	return props.window.status.name === 'FULL';
});

const ctaButtonText = computed((): string => {
	return canCreateReservation.value || isDisabled.value ? 'Create a reservation' : 'Contact Investor Relations';
});

const ctaButtonClasses = computed((): string => {
	if (isDisabled.value) {
		return 'disabled';
	} else {
		return props.isNextWindow ? 'button-fundrise-pro' : 'button-outline';
	}
});

async function handleCtaClick(): Promise<void> {
	if (canCreateReservation.value) {
		investStore.selectedWindow = props.window;

		if (route.query.trigger) {
			investmentTriggerStore.investmentTrigger = mapInvestmentTrigger(
				route.query.trigger as InvestmentTriggerQuery
			);
		}

		await router.push({
			name: props.createReservationLink.router,
			params: props.createReservationLink.params,
			query: props.createReservationLink.query
		});
	} else {
		await router.push({ name: 'contact' });
	}
}
</script>
<style lang="scss">
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;

.reservation-window-card {
	.window-progress-meter {
		.progress {
			background-color: token('data-primary-dark');
		}
	}
}
</style>
